import React, { useEffect, useState } from "react";
import { api, formatDate, formatCurrency } from "../../lib/api";
import { ShoppingBag, CheckCircle2, Clock, XCircle, Trash2, RefreshCcw, PackageCheck, Undo2 } from "lucide-react";
import { toast } from "sonner";

const statusStyles = (s) => {
  if (s === "paid" || s === "complete") return { bg: "rgba(74,93,78,0.12)", color: "var(--brand-sage)" };
  if (s === "expired" || s === "failed") return { bg: "rgba(232,163,124,0.2)", color: "var(--brand-orange)" };
  return { bg: "rgba(160,82,45,0.1)", color: "var(--text-accent)" };
};

const StatusIcon = ({ status }) => {
  if (status === "paid" || status === "complete") return <CheckCircle2 size={14} />;
  if (status === "expired" || status === "failed") return <XCircle size={14} />;
  return <Clock size={14} />;
};

export default function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/orders");
      setOrders(data);
    } catch {
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (o) => {
    const itemsSummary = (o.items || []).map((i) => `${i.name} ×${i.quantity}`).join(", ") || "(no line items)";
    if (!confirm(`Are you sure you want to delete this order?\n\nAmount: ${formatCurrency(o.amount, (o.currency || "usd").toUpperCase())}\nStatus: ${o.payment_status}\nItems: ${itemsSummary}\n\nIt will move to Trash and auto-delete after 30 days. Stripe still has the official record.`)) return;
    try {
      await api.delete(`/admin/orders/${o.session_id}`);
      setOrders((prev) => prev.filter((x) => x.session_id !== o.session_id));
      toast.success("Moved to Trash");
    } catch {
      toast.error("Delete failed");
    }
  };

  const refresh = async (o) => {
    try {
      const { data } = await api.post(`/admin/orders/${o.session_id}/refresh`);
      await load();
      const updated = (data?.fields_updated || []).length;
      if (updated > 0) {
        toast.success("Customer info refreshed from Stripe");
      } else {
        toast.info("Stripe has no customer details for this order yet");
      }
    } catch {
      toast.error("Refresh failed");
    }
  };

  const toggleFulfilled = async (o) => {
    try {
      const { data } = await api.post(`/admin/orders/${o.session_id}/fulfill`);
      setOrders((prev) =>
        prev.map((x) => (x.session_id === o.session_id ? { ...x, fulfillment_status: data.fulfillment_status } : x))
      );
      toast.success(data.fulfillment_status === "fulfilled" ? "Marked as fulfilled" : "Marked as unfulfilled");
    } catch {
      toast.error("Could not update fulfillment status");
    }
  };

  const refund = async (o) => {
    const itemsSummary = (o.items || []).map((i) => `${i.name} ×${i.quantity}`).join(", ");
    if (!confirm(
      `Refund this order on Stripe?\n\nAmount: ${formatCurrency(o.amount, (o.currency || "usd").toUpperCase())}\nCustomer: ${o.customer_name || o.customer_email || "—"}\nItems: ${itemsSummary}\n\nThis will issue a FULL refund through Stripe and restock the items. This action cannot be undone.`
    )) return;
    try {
      const { data } = await api.post(`/admin/orders/${o.session_id}/refund`);
      setOrders((prev) =>
        prev.map((x) => (x.session_id === o.session_id ? { ...x, fulfillment_status: "refunded", refund_id: data.refund_id } : x))
      );
      toast.success(`Refunded ${formatCurrency(data.amount_refunded)} on Stripe`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refund failed");
    }
  };

  const paidTotal = orders
    .filter((o) => o.payment_status === "paid")
    .reduce((s, o) => s + (o.amount || 0), 0);

  return (
    <div data-testid="orders-tab">
      <header className="mb-8">
        <p className="font-script text-2xl" style={{ color: "var(--text-accent)" }}>
          recent activity
        </p>
        <h1 className="font-display text-4xl" style={{ color: "var(--text-primary)" }}>
          Orders
        </h1>
      </header>

      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <div
          className="bg-white rounded-2xl p-5"
          style={{ border: "1px solid var(--border-soft)" }}
        >
          <p className="text-xs uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>
            Paid orders
          </p>
          <p className="font-display text-3xl mt-1" style={{ color: "var(--brand-sage)" }} data-testid="orders-paid-count">
            {loading ? "…" : orders.filter((o) => o.payment_status === "paid").length}
          </p>
        </div>
        <div
          className="bg-white rounded-2xl p-5"
          style={{ border: "1px solid var(--border-soft)" }}
        >
          <p className="text-xs uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>
            Revenue (paid)
          </p>
          <p className="font-display text-3xl mt-1" style={{ color: "var(--text-primary)" }} data-testid="orders-paid-revenue">
            {loading ? "…" : formatCurrency(paidTotal)}
          </p>
        </div>
        <div
          className="bg-white rounded-2xl p-5"
          style={{ border: "1px solid var(--border-soft)" }}
        >
          <p className="text-xs uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>
            Total sessions
          </p>
          <p className="font-display text-3xl mt-1" style={{ color: "var(--text-primary)" }} data-testid="orders-total-count">
            {loading ? "…" : orders.length}
          </p>
        </div>
      </div>

      {loading ? (
        <p className="font-script text-xl" style={{ color: "var(--text-secondary)" }}>
          loading…
        </p>
      ) : orders.length === 0 ? (
        <p style={{ color: "var(--text-secondary)" }}>No orders yet.</p>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => {
            const styles = statusStyles(o.payment_status);
            return (
              <article
                key={o.session_id}
                className="bg-white rounded-2xl p-5"
                style={{ border: "1px solid var(--border-soft)" }}
                data-testid={`order-${o.session_id}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <ShoppingBag size={18} style={{ color: "var(--brand-sage)" }} />
                    <span
                      className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full inline-flex items-center gap-1"
                      style={{ backgroundColor: styles.bg, color: styles.color }}
                    >
                      <StatusIcon status={o.payment_status} />
                      {o.payment_status}
                    </span>
                    <span className="font-display text-xl" style={{ color: "var(--text-primary)" }}>
                      {formatCurrency(o.amount, (o.currency || "usd").toUpperCase())}
                    </span>
                  </div>
                  <span className="text-xs" style={{ color: "var(--text-secondary)" }}>
                    {formatDate(o.created_at)}
                  </span>
                </div>
                <ul className="mt-3 text-sm space-y-1" style={{ color: "var(--text-secondary)" }}>
                  {(o.items || []).map((it, idx) => (
                    <li key={`${o.session_id}-${it.product_id || idx}`}>
                      <span style={{ color: "var(--text-primary)", fontWeight: 600 }}>{it.name}</span> ×{it.quantity} · {formatCurrency(it.unit_price)}
                    </li>
                  ))}
                </ul>
                {o.delivery_method && (
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <span
                      className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full inline-flex items-center gap-1"
                      style={{
                        backgroundColor: o.delivery_method === "ship" ? "rgba(232,163,124,0.15)" : "rgba(74,93,78,0.12)",
                        color: o.delivery_method === "ship" ? "var(--brand-orange)" : "var(--brand-sage)",
                      }}
                    >
                      {o.delivery_method === "ship" ? "📦 Ship" : "📍 Pickup"}
                    </span>
                    {o.shipping_fee > 0 && (
                      <span className="text-xs" style={{ color: "var(--text-secondary)" }}>
                        +{formatCurrency(o.shipping_fee)} shipping
                      </span>
                    )}
                    {o.payment_status === "paid" && o.fulfillment_status && (
                      <span
                        className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full inline-flex items-center gap-1"
                        style={{
                          backgroundColor:
                            o.fulfillment_status === "fulfilled" ? "rgba(74,93,78,0.15)" :
                            o.fulfillment_status === "refunded" ? "rgba(160,82,45,0.15)" :
                            "rgba(232,163,124,0.10)",
                          color:
                            o.fulfillment_status === "fulfilled" ? "var(--brand-sage)" :
                            o.fulfillment_status === "refunded" ? "var(--brand-orange)" :
                            "var(--text-secondary)",
                        }}
                        data-testid={`order-fulfillment-${o.session_id}`}
                      >
                        {o.fulfillment_status === "fulfilled" ? "✓ Fulfilled" :
                         o.fulfillment_status === "refunded" ? "↩ Refunded" :
                         "○ Unfulfilled"}
                      </span>
                    )}
                  </div>
                )}

                {(o.customer_name || o.customer_email || o.shipping_address) && (
                  <div
                    className="mt-4 pt-3 grid sm:grid-cols-2 gap-x-6 gap-y-3 text-xs"
                    style={{ borderTop: "1px dashed var(--border-soft)" }}
                  >
                    <div>
                      <p className="uppercase tracking-wider font-semibold mb-1" style={{ color: "var(--text-secondary)" }}>
                        Customer
                      </p>
                      {o.customer_name && (
                        <p style={{ color: "var(--text-primary)", fontWeight: 600 }} data-testid={`order-customer-name-${o.session_id}`}>
                          {o.customer_name}
                        </p>
                      )}
                      {o.customer_email && (
                        <p style={{ color: "var(--text-secondary)" }}>
                          <a href={`mailto:${o.customer_email}`} className="hover:underline">
                            {o.customer_email}
                          </a>
                        </p>
                      )}
                      {o.customer_phone && (
                        <p style={{ color: "var(--text-secondary)" }}>
                          <a href={`tel:${o.customer_phone}`} className="hover:underline">
                            {o.customer_phone}
                          </a>
                        </p>
                      )}
                    </div>
                    {o.delivery_method === "ship" && o.shipping_address && o.shipping_address.line1 && (
                      <div data-testid={`order-shipping-${o.session_id}`}>
                        <p className="uppercase tracking-wider font-semibold mb-1" style={{ color: "var(--text-secondary)" }}>
                          Ship to
                        </p>
                        <p style={{ color: "var(--text-primary)" }}>
                          {o.shipping_name && <>{o.shipping_name}<br /></>}
                          {o.shipping_address.line1}
                          {o.shipping_address.line2 && <><br />{o.shipping_address.line2}</>}
                          <br />
                          {[o.shipping_address.city, o.shipping_address.state, o.shipping_address.postal_code].filter(Boolean).join(", ")}
                          {o.shipping_address.country && <><br />{o.shipping_address.country}</>}
                        </p>
                      </div>
                    )}
                  </div>
                )}
                {o.subscriber_email && (
                  <p className="text-xs mt-3" style={{ color: "var(--text-secondary)" }}>
                    Customer subscribed to updates: <strong>{o.subscriber_email}</strong>
                  </p>
                )}
                <p className="text-[10px] mt-2 font-mono" style={{ color: "var(--text-secondary)" }}>
                  {o.session_id}
                </p>
                <div className="flex justify-end gap-2 mt-3 flex-wrap">
                  {o.payment_status === "paid" && o.fulfillment_status !== "refunded" && (
                    <button
                      onClick={() => toggleFulfilled(o)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold"
                      style={{
                        border: "1px solid var(--border-soft)",
                        color: o.fulfillment_status === "fulfilled" ? "var(--text-secondary)" : "var(--brand-sage)",
                        backgroundColor: o.fulfillment_status === "fulfilled" ? "rgba(74,93,78,0.08)" : "transparent",
                      }}
                      data-testid={`fulfill-order-${o.session_id}`}
                      title={o.fulfillment_status === "fulfilled" ? "Mark as not yet fulfilled" : "Mark as fulfilled (shipped or handed off)"}
                    >
                      <PackageCheck size={12} />
                      {o.fulfillment_status === "fulfilled" ? "Unfulfill" : "Mark fulfilled"}
                    </button>
                  )}
                  {o.payment_status === "paid" && o.fulfillment_status !== "refunded" && (
                    <button
                      onClick={() => refund(o)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold"
                      style={{ border: "1px solid var(--border-soft)", color: "var(--brand-orange)" }}
                      data-testid={`refund-order-${o.session_id}`}
                      title="Issue a FULL refund on Stripe and restock"
                    >
                      <Undo2 size={12} />
                      Refund
                    </button>
                  )}
                  {o.payment_status === "paid" && (
                    <button
                      onClick={() => refresh(o)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold"
                      style={{ border: "1px solid var(--border-soft)", color: "var(--brand-sage)" }}
                      data-testid={`refresh-order-${o.session_id}`}
                      title="Re-pull customer + shipping details from Stripe"
                    >
                      <RefreshCcw size={12} />
                      Refresh info
                    </button>
                  )}
                  <button
                    onClick={() => remove(o)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold"
                    style={{ border: "1px solid var(--border-soft)", color: "var(--brand-orange)" }}
                    data-testid={`delete-order-${o.session_id}`}
                  >
                    <Trash2 size={12} />
                    Delete
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
